import { useEffect, useCallback, useState } from 'react';
import { getClient } from 'lane-shared/apollo';
import { ApolloError } from '@apollo/client';
import { getReservablesOnChannelQuery } from 'graphql-queries';
import { ReservableEdge } from 'graphql-query-contracts';

type Props = {
  channelId: string;
  pageSize: number;
  rangeStart: Date;
  rangeEnd: Date;
  includeCancelled?: boolean;
};

export function useReservablesOnChannel({
  channelId,
  pageSize,
  rangeStart,
  rangeEnd,
  includeCancelled = false,
}: Props) {
  const client = getClient();
  const [error, setError] = useState<ApolloError | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [reservablesOnChannel, setReservablesOnChannel] = useState<
    ReservableEdge[]
  >([]);

  const fetchReservablesOnChannel = useCallback(async () => {
    setIsLoading(true);

    try {
      const { data, error } = await client.query<{
        channel: { reservablesOnChannel: { edges: ReservableEdge[] } };
      }>({
        query: getReservablesOnChannelQuery,
        fetchPolicy: 'network-only',
        variables: {
          channelId,
          pageSize,
          reservablesSearch: {
            unpublishDate: {
              value: rangeStart,
            },
          },
          startDate: rangeStart,
          endDate: rangeEnd,
          includeCancelled,
        },
      });

      if (error) {
        throw error;
      }

      setReservablesOnChannel(data.channel?.reservablesOnChannel?.edges || []);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [client, channelId, pageSize, rangeStart, rangeEnd, includeCancelled]);

  useEffect(() => {
    fetchReservablesOnChannel();
  }, [fetchReservablesOnChannel]);

  return {
    reservablesOnChannel,
    fetchReservablesOnChannel,
    isLoading,
    isInitialLoad: isLoading && !hasLoadedOnce,
    error,
  };
}
